import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Search } from 'lucide-react';
import Header from '../Common/Header';
import Footer from '../Common/Footer';
import Input from '@ajat/design-system/Input';
import Button from '@ajat/design-system/Button';
import FAQItem from '@ajat/design-system/FAQItem';
import EmptyState from '@ajat/design-system/EmptyState';
import { Sidebar, buildHelpTopics, getActiveTopicId, useFAQ } from '../../Common/HelpCenter';
import { CATEGORIES, QUESTIONS } from './faqData';
import { HELP_LABELS, HELP_ROUTES } from './helpConfig';
import '../../Common/HelpCenter/HelpCenter.css';
import '../../Common/HelpCenter/FAQ.css';

const CLSFIFAQ = () => {
  const navigate = useNavigate();
  const {
    query, setQuery,
    activeCategory, setActiveCategory,
    expanded, toggle,
    filtered,
    isSearching,
    hasResults,
  } = useFAQ({ categories: CATEGORIES, questions: QUESTIONS });

  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-brand');
    document.documentElement.setAttribute('data-brand', 'CLS');
    return () => {
      if (prev) document.documentElement.setAttribute('data-brand', prev);
      else document.documentElement.removeAttribute('data-brand');
    };
  }, []);

  const sectionTitle = isSearching
    ? 'Hakutulokset'
    : activeCategory
      ? CATEGORIES.find((c) => c.id === activeCategory)?.label
      : 'Yleisimmät kysymykset';

  const topics = buildHelpTopics({
    context: 'faq',
    categories: CATEGORIES,
    labels: HELP_LABELS,
    routes: HELP_ROUTES,
    navigate,
    setActiveCategory,
    setQuery,
  });
  const activeId = getActiveTopicId({ context: 'faq', activeCategory, isSearching });

  return (
    <div className="helpcenter">
      <Header showTopBar={false} />
      <main className="helpcenter__main">
        <div className="helpcenter__container">
          <div className="helpcenter__list-col">
            <h1 className="helpcenter__title typo-h1">Miten voimme auttaa?</h1>

            <Input
              type="text"
              icon={Search}
              label="Hae usein kysytyistä kysymyksistä"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              showClear={Boolean(query)}
            />

            <h2 className="helpcenter__section-title typo-h3">{sectionTitle}</h2>

            {isSearching ? (
              <p className="faq-page__results-count typo-body-sm">{filtered.length} tulosta</p>
            ) : null}

            {hasResults ? (
              <div className="faq-page__list">
                {filtered.map((item) => (
                  <FAQItem
                    key={item.id}
                    id={item.id}
                    question={item.question}
                    answer={item.answer}
                    expanded={expanded.has(item.id)}
                    onToggle={() => toggle(item.id)}
                  />
                ))}
              </div>
            ) : (
              <EmptyState size="inline" title="Ei tuloksia" message="Yritä hakea toisilla sanoilla tai kirjoita meille." />
            )}

            {/* Contact / Reclamation Card */}
            <div className="faq-page__contact-card">
              <h3 className="faq-page__contact-card-title typo-h3">Etkö löytänyt vastausta?</h3>
              <p className="faq-page__contact-card-text typo-body">
                {HELP_ROUTES.claims
                  ? 'Autamme sinua nopeasti — ota meihin suoraan yhteyttä tai lähetä reklamaatio.'
                  : 'Autamme sinua nopeasti — ota meihin suoraan yhteyttä.'}
              </p>
              <div className="faq-page__contact-card-ctas">
                <Button
                  variant="black"
                  size="secondary"
                  iconRight={<ArrowRight size={18} />}
                  onClick={() => navigate('/clothing/cls-fi/contact')}
                >
                  OTA YHTEYTTÄ
                </Button>
                {HELP_ROUTES.claims ? (
                  <Button
                    variant="outline"
                    size="secondary"
                    iconRight={<ArrowRight size={18} />}
                    onClick={() => navigate(HELP_ROUTES.claims)}
                  >
                    REKLAMAATIO
                  </Button>
                ) : null}
              </div>
            </div>
          </div>

          <Sidebar
            title={HELP_LABELS.sidebarTitle}
            topics={topics}
            activeId={activeId}
            dividerBeforeId={HELP_ROUTES.claims ? 'claims' : 'contact'}
          />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CLSFIFAQ;
